import React, {useState} from "react";

import {
    useAutoplaySequence,
} from "../../../../../presentation/public-site/use-autoplay-sequence";
import {
    crossDockingDefaultStageId,
    crossDockingStages,
    crossDockingWorkspaceItems,
} from "../../content/cross-docking";

const CROSS_DOCKING_AUTOPLAY_INTERVAL_MS = 5000;

const CROSS_DOCKING_STAGE_IDS =
    crossDockingStages.map(
        ({id}) => id
    );

function CrossDockingStageIcon({type}) {
    if (type === "distribution") {
        return (
            <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <rect
                    x="10"
                    y="3.5"
                    width="8"
                    height="6"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
                <rect
                    x="3.5"
                    y="18.5"
                    width="8"
                    height="6"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
                <rect
                    x="16.5"
                    y="18.5"
                    width="8"
                    height="6"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
                <path
                    d="M14 9.5V13.5M14 13.5H7.5V18.5M14 13.5H20.5V18.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    }

    if (type === "dispatch") {
        return (
            <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
            >
                <path
                    d="M3.5 7H16V20H3.5V7ZM16 11H21L24.5 15V20H16V11Z"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                />
                <circle
                    cx="8"
                    cy="20.5"
                    r="2.5"
                    fill="white"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
                <circle
                    cx="20.5"
                    cy="20.5"
                    r="2.5"
                    fill="white"
                    stroke="currentColor"
                    strokeWidth="1.7"
                />
            </svg>
        );
    }

    return (
        <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M4 9L14 4L24 9V21.5H4V9Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
            />
            <path
                d="M9 21.5V13H19V21.5M10.5 9.5H17.5"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default function CrossDockingFlow({t}) {
    const [activeStageId, setActiveStageId] =
        useState(crossDockingDefaultStageId);

    const {
        targetRef: autoplayTargetRef,
        isAutoplaying,
        selectItem: selectStage,
        stopAutoplay,
    } = useAutoplaySequence({
        itemIds:
            CROSS_DOCKING_STAGE_IDS,
        activeItemId: activeStageId,
        setActiveItemId: setActiveStageId,
        intervalMs:
            CROSS_DOCKING_AUTOPLAY_INTERVAL_MS,
        threshold: 0.2,
        rootMargin:
            "0px 0px -8% 0px",
    });

    const activeStage =
        crossDockingStages.find(
            (stage) =>
                stage.id === activeStageId
        ) || crossDockingStages[0];

    return (
        <article
            className="cross-docking-flow"
            ref={autoplayTargetRef}
        >
            <div className="cross-docking-flow__header">
                <div>
                    <span className="cross-docking-flow__demo">
                        <span/>
                        {t(
                            "main.landing.crossDocking.flow.eyebrow"
                        )}
                    </span>

                    <strong>
                        {t(
                            "main.landing.crossDocking.flow.title"
                        )}
                    </strong>
                </div>

                <span className="cross-docking-flow__status landing-status-pill">
                    {t(
                        "main.landing.crossDocking.flow.status"
                    )}
                </span>
            </div>

            <div
                className={`cross-docking-flow__stages${
                    isAutoplaying
                        ? " cross-docking-flow__stages--autoplaying"
                        : ""
                }`}
                role="tablist"
                aria-label={t(
                    "main.landing.crossDocking.tabsAriaLabel"
                )}
            >
                {crossDockingStages.map((stage) => {
                    const active =
                        stage.id === activeStageId;

                    return (
                        <button
                            className={`cross-docking-stage${
                                active
                                    ? " cross-docking-stage--active"
                                    : ""
                            }`}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            aria-controls="cross-docking-stage-details"
                            id={`cross-docking-stage-${stage.id}`}
                            onClick={() =>
                                selectStage(stage.id)
                            }
                            onFocus={stopAutoplay}
                            key={stage.id}
                        >
                            <span className="cross-docking-stage__top">
                                <span className="cross-docking-stage__icon">
                                    <CrossDockingStageIcon
                                        type={stage.icon}
                                    />
                                </span>

                                <span className="cross-docking-stage__number">
                                    {stage.number}
                                </span>
                            </span>

                            <span className="cross-docking-stage__content">
                                <strong>
                                    {t(stage.titleKey)}
                                </strong>

                                <span>
                                    {t(
                                        stage.descriptionKey
                                    )}
                                </span>
                            </span>

                            <span
                                className={`cross-docking-stage__status cross-docking-stage__status--${stage.statusTone}`}
                            >
                                {t(stage.statusKey)}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div
                className="cross-docking-details"
                id="cross-docking-stage-details"
                role="tabpanel"
                aria-labelledby={`cross-docking-stage-${activeStage.id}`}
                key={activeStage.id}
            >
                <div className="cross-docking-details__header">
                    <span>
                        {t(
                            "main.landing.crossDocking.stageLabel",
                            {
                                number:
                                    activeStage.number,
                            }
                        )}
                    </span>

                    <strong>
                        {t(activeStage.titleKey)}
                    </strong>
                </div>

                <dl className="cross-docking-details__grid">
                    <div>
                        <dt>
                            {t(
                                "main.landing.crossDocking.details.status"
                            )}
                        </dt>
                        <dd>
                            {t(activeStage.statusKey)}
                        </dd>
                    </div>

                    <div>
                        <dt>
                            {t(
                                "main.landing.crossDocking.details.operation"
                            )}
                        </dt>
                        <dd>
                            {t(activeStage.operationKey)}
                        </dd>
                    </div>

                    <div>
                        <dt>
                            {t(
                                "main.landing.crossDocking.details.platform"
                            )}
                        </dt>
                        <dd>
                            {t(activeStage.platformKey)}
                        </dd>
                    </div>

                    <div>
                        <dt>
                            {t(
                                "main.landing.crossDocking.details.result"
                            )}
                        </dt>
                        <dd>
                            {t(activeStage.resultKey)}
                        </dd>
                    </div>
                </dl>
            </div>

            <div className="cross-docking-workspace landing-control-summary">
                <div className="cross-docking-workspace__copy">
                    <span>
                        {t(
                            "main.landing.crossDocking.workspace.eyebrow"
                        )}
                    </span>

                    <strong>
                        {t(
                            "main.landing.crossDocking.workspace.title"
                        )}
                    </strong>
                </div>

                <div className="cross-docking-workspace__items">
                    {crossDockingWorkspaceItems.map(
                        (item) => (
                            <span
                                className={`cross-docking-workspace__item cross-docking-workspace__item--${item.tone}`}
                                key={item.id}
                            >
                                {t(item.labelKey)}
                            </span>
                        )
                    )}
                </div>
            </div>
        </article>
    );
}
